import { FlaskConical, LoaderCircle, Pill, Share, TriangleAlert, Zap } from 'lucide-react';

import { COPILOT_ORDERS_CHIP_ANCHOR } from '@/app/hints';
import { Button } from '@/shared/ui/shadcn/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/shared/ui/shadcn/hover-card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';
import { useHintAnchor } from '@/shared/ui/primitives';

import { ActionChip } from './ActionChip';

export interface OrdersChipMenuProps {
  disabled: boolean;
  loading?: boolean;
  hasInsurance?: boolean;
  canPrescribe?: boolean;
  onPrescriptionClick?: () => void;
  onLabClick?: () => void;
}

export function OrdersChipMenu({
  disabled,
  loading = false,
  hasInsurance = true,
  canPrescribe = true,
  onPrescriptionClick,
  onLabClick,
}: OrdersChipMenuProps) {
  // Radix Slot composes refs, so this lands on the trigger element alongside HoverCard's own ref.
  const ordersAnchorRef = useHintAnchor(COPILOT_ORDERS_CHIP_ANCHOR);

  if (disabled) {
    return <ActionChip icon={Zap} label="Orders" showLabel disabled />;
  }

  return (
    <HoverCard openDelay={0} closeDelay={50}>
      <HoverCardTrigger asChild>
        <span ref={ordersAnchorRef}>
          <ActionChip icon={Zap} label="Orders" showLabel />
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" side="top" className="w-44 p-1">
        <div className="flex flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-8 w-full justify-start px-2"
                type="button"
                variant="ghost"
                disabled={loading || !canPrescribe}
                onClick={onPrescriptionClick}
              >
                <Pill className="size-4" aria-hidden />
                Prescription
                {loading && (
                  <LoaderCircle
                    className="ml-auto size-3.5 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                )}
                {!loading && !canPrescribe && (
                  <TriangleAlert className="ml-auto size-3.5 text-amber-500" aria-hidden />
                )}
              </Button>
            </TooltipTrigger>
            {!loading && !canPrescribe && (
              <TooltipContent side="right" className="max-w-56 text-xs">
                DoseSpot setup incomplete — prescriptions are not available
              </TooltipContent>
            )}
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-8 w-full justify-start px-2"
                type="button"
                variant="ghost"
                disabled={loading || !hasInsurance}
                onClick={onLabClick}
              >
                <FlaskConical className="size-4" aria-hidden />
                Lab
                {loading && (
                  <LoaderCircle
                    className="ml-auto size-3.5 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                )}
                {!loading && !hasInsurance && (
                  <TriangleAlert className="ml-auto size-3.5 text-amber-500" aria-hidden />
                )}
              </Button>
            </TooltipTrigger>
            {!loading && !hasInsurance && (
              <TooltipContent side="right" className="max-w-56 text-xs">
                No insurance info — labs may not be submittable
              </TooltipContent>
            )}
          </Tooltip>
          <Button className="h-8 w-full justify-start px-2" type="button" variant="ghost" disabled>
            <Share className="size-4" aria-hidden />
            Referral
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
